import { CSSProperties, FC, ReactNode } from 'react';
import { Breadcrumb, Col, Flex, Row } from 'antd';
import { TbChevronRight } from 'react-icons/tb';
import { Link } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

export interface ResourceHeaderBreadcrumbItem {
    content?: ReactNode;
    icon?: ReactNode;
    key?: string;
    label?: ReactNode;
    onClick?: () => void;
    to?: string;
}

interface ResourceHeaderProps {
    breadcrumbs: ResourceHeaderBreadcrumbItem[];
    leftActions?: ReactNode;
    rightActions?: ReactNode;
    style?: CSSProperties;
}

export const ResourceHeaderBreadcrumbContent: FC<Omit<ResourceHeaderBreadcrumbItem, 'content' | 'key'>> = ({
    icon,
    label,
    onClick,
    to,
}): JSX.Element => {
    const content = (
        <span
            style={{
                alignItems: 'center',
                display: 'inline-flex',
                fontWeight: 500,
                gap: icon ? '0.375rem' : 0,
                maxWidth: '100%',
                minWidth: 0,
                verticalAlign: 'middle',
            }}
        >
            {icon && (
                <span style={{ alignItems: 'center', display: 'inline-flex', flex: 'none', lineHeight: 0 }}>
                    {icon}
                </span>
            )}
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {label}
            </span>
        </span>
    );

    if (to) {
        return <Link to={to}>{content}</Link>;
    }

    if (onClick) {
        return (
            <button
                type='button'
                onClick={onClick}
                style={{
                    background: 'none',
                    border: 0,
                    color: 'inherit',
                    cursor: 'pointer',
                    font: 'inherit',
                    maxWidth: '100%',
                    padding: 0,
                }}
            >
                {content}
            </button>
        );
    }

    return <>{content}</>;
};

const ResourceHeader: FC<ResourceHeaderProps> = ({ breadcrumbs, leftActions, rightActions, style }): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <Row
            align='middle'
            style={{
                borderBottom: `1px solid ${token.colorBorder}`,
                padding: '1.5rem',
                width: '100%',
                ...style,
            }}
        >
            <Col flex='auto' style={{ minWidth: 0 }}>
                <Flex align='center' gap='0.75rem' style={{ minWidth: 0 }}>
                    <Breadcrumb separator={<TbChevronRight style={{ verticalAlign: '-0.15rem' }} />}>
                        {breadcrumbs.map((item, index) => (
                            <Breadcrumb.Item key={item.key ?? index}>
                                {item.content ?? (
                                    <ResourceHeaderBreadcrumbContent
                                        icon={item.icon}
                                        label={item.label}
                                        onClick={item.onClick}
                                        to={item.to}
                                    />
                                )}
                            </Breadcrumb.Item>
                        ))}
                    </Breadcrumb>
                    {leftActions}
                </Flex>
            </Col>
            {rightActions && (
                <Col flex='none'>
                    <Flex align='center' gap='0.5rem'>
                        {rightActions}
                    </Flex>
                </Col>
            )}
        </Row>
    );
};

export default ResourceHeader;
